import {
  ArrowRight,
  BarChart3,
  ChevronDown,
  Hash,
  Loader2,
  Pencil,
  Sparkles,
  Table2,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";
import { ChartView } from "../../components/ChartView";
import type { DataDomain } from "../../services/dataDomain";
import type { PanelEncoding } from "../../types";
import {
  BOX_KINDS,
  isPlanStop,
  MAX_QUESTION_CHARS,
  questionProblem,
  type BoxKind,
  type CanvasBox,
} from "./canvasModel";
import { PlanOutcome } from "./PlanOutcome";
import { DASHBOARD_BOX_KINDS, dashboardBoxPrompt } from "./prompts";
import { QuestionField } from "./QuestionField";

const EMPTY_ENCODING: PanelEncoding = { x: "", y: [], series: "" };

const KIND_ICONS: Record<BoxKind, LucideIcon> = {
  headline: Hash,
  trend: TrendingUp,
  comparison: BarChart3,
  breakdown: Table2,
  auto: Sparkles,
};

interface CanvasTileProps {
  box: CanvasBox;
  /** After the first submit, a box in draft is asked on its own. */
  submitted: boolean;
  /** Chooses domain-specific examples in the empty prompt. */
  domain?: DataDomain | null;
  onQuestion: (question: string) => void;
  onKind: (kind: BoxKind) => void;
  onAsk: () => void;
  onRetry: () => void;
  onEdit: () => void;
  onChooseOption: (option: string) => void;
  onKeepCandidate: (index: number) => void;
}

/** One box of the dashboard canvas, rendered for whichever phase it is in. */
export function CanvasTile({
  box,
  submitted,
  domain = null,
  onQuestion,
  onKind,
  onAsk,
  onRetry,
  onEdit,
  onChooseOption,
  onKeepCandidate,
}: CanvasTileProps) {
  // Not shown: the position tells apart two boxes of the same kind for a
  // screen reader, and names the box in its buttons.
  const label = `Box ${box.slot + 1}`;
  const title = box.panel?.title;

  return (
    <section
      className={`canvas-tile ${box.phase} ${box.question.trim() ? "filled" : "empty"}`}
      data-kind={box.kind}
      aria-label={label}
      aria-busy={box.phase === "planning" || box.phase === "running"}
    >
      {box.phase === "draft" && (
        <DraftBody
          box={box}
          label={label}
          domain={domain}
          submitted={submitted}
          onQuestion={onQuestion}
          onKind={onKind}
          onAsk={onAsk}
        />
      )}

      {(box.phase === "planning" || box.phase === "running") && (
        <div className="canvas-tile-status" role="status">
          <Loader2 size={22} className="spin" aria-hidden="true" />
          <span>
            {box.phase === "planning"
              ? "Planning the chart…"
              : `Running ${title ? `“${title}”` : "the governed query"}…`}
          </span>
        </div>
      )}

      {box.phase === "ready" && box.panel && (
        <>
          <header className="canvas-tile-head">
            <h3 title={box.question}>{title}</h3>
            <button
              type="button"
              className="icon-button"
              onClick={onEdit}
              aria-label={`Change the question in ${label}`}
              title="Change question"
            >
              <Pencil size={15} />
            </button>
          </header>
          <div className="canvas-tile-chart">
            <ChartView
              panelId={box.panel.id ?? box.id}
              chartType={box.panel.chartType}
              encoding={{ ...EMPTY_ENCODING, ...box.panel.encoding }}
              result={box.result ?? undefined}
            />
          </div>
        </>
      )}

      {isPlanStop(box.phase) && (
        <PlanOutcome
          phase={box.phase}
          label={label}
          question={box.question}
          message={box.message}
          options={box.options}
          candidates={box.candidates}
          retryable={box.retryable}
          onChooseOption={onChooseOption}
          onKeepCandidate={onKeepCandidate}
          onRetry={onRetry}
          onEdit={onEdit}
        />
      )}
    </section>
  );
}

function DraftBody({
  box,
  label,
  domain,
  submitted,
  onQuestion,
  onKind,
  onAsk,
}: {
  box: CanvasBox;
  label: string;
  domain: DataDomain | null;
  submitted: boolean;
  onQuestion: (question: string) => void;
  onKind: (kind: BoxKind) => void;
  onAsk: () => void;
}) {
  const length = box.question.length;
  const invalid = questionProblem(box.question) !== null;
  const prompt = dashboardBoxPrompt(box.kind, domain);
  const Icon = KIND_ICONS[box.kind];
  return (
    <div className="canvas-tile-draft">
      <div className="canvas-tile-kind">
        <span className="canvas-tile-icon" aria-hidden="true">
          <Icon size={15} />
        </span>
        <span className="canvas-tile-kind-select">
          <select
            aria-label={`What ${label} shows`}
            value={box.kind}
            onChange={(event) => onKind(event.target.value as BoxKind)}
          >
            {BOX_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {DASHBOARD_BOX_KINDS[kind].label}
              </option>
            ))}
          </select>
          <ChevronDown size={14} aria-hidden="true" />
        </span>
      </div>
      <QuestionField
        label={`Question for ${label}`}
        value={box.question}
        prompt={prompt}
        showLabel={false}
        maxLength={MAX_QUESTION_CHARS}
        onChange={onQuestion}
        onSubmit={submitted && !invalid ? onAsk : undefined}
      />
      {(length > 0 || submitted) && (
        <div className="canvas-tile-draft-foot">
          <small aria-live="polite">
            {length > MAX_QUESTION_CHARS * 0.8 ? `${length}/${MAX_QUESTION_CHARS}` : ""}
          </small>
          {submitted && (
            <button
              type="button"
              className="button primary small"
              onClick={onAsk}
              disabled={invalid}
              aria-label={`Ask ${label}`}
            >
              Ask <ArrowRight size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
